import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface AuraSummaryProps {
  responses: {
    participationcomfort: string[];
    learningenvironment: string[];
    communicationstyle: string[];
  };
  onEditSurvey?: () => void;
  onBackToSelection?: () => void;
}

const calculateAuraColors = (responses: AuraSummaryProps['responses']) => {
  const colors = {
    participationcomfort: {
      color: '#FF6B6B', // Coral Red
      intensity: 0,
      label: 'Participation Style',
      description: ''
    },
    learningenvironment: {
      color: '#4ECDC4', // Turquoise
      intensity: 0,
      label: 'Learning Environment',
      description: ''
    },
    communicationstyle: {
      color: '#9B59B6', // Amethyst Purple
      intensity: 0,
      label: 'Communication Style',
      description: ''
    }
  };

  // Calculate intensity and determine descriptions for each category
  Object.entries(responses).forEach(([category, answers]) => {
    const colorKey = category.toLowerCase() as keyof typeof colors;
    if (colors[colorKey] && Array.isArray(answers)) {
      let score = 0;
      
      // Participation Style
      if (category === 'participationcomfort') {
        const prefersFrontSeating = answers.includes('Strongly Agree') || answers.includes('Agree');
        const prefersGroupWork = answers.some(a => a.includes('group') && (a === 'Strongly Agree' || a === 'Agree'));
        
        if (prefersFrontSeating && prefersGroupWork) {
          colors[colorKey].description = "Active Engager - Thrives in front-row participation and collaborative activities";
          score = 0.9;
        } else if (prefersFrontSeating) {
          colors[colorKey].description = "Independent Contributor - Prefers direct engagement with instructor";
          score = 0.7;
        } else if (prefersGroupWork) {
          colors[colorKey].description = "Collaborative Participant - Excels in peer-to-peer learning settings";
          score = 0.6;
        } else {
          colors[colorKey].description = "Reflective Observer - Prefers thoughtful observation before participation";
          score = 0.4;
        }
      }
      
      // Learning Environment
      else if (category === 'learningenvironment') {
        const prefersStructured = answers.some(a => a.includes('structured') && (a === 'Strongly Agree' || a === 'Agree'));
        const prefersInteractive = answers.some(a => a.includes('interactive') && (a === 'Strongly Agree' || a === 'Agree'));
        
        if (prefersStructured && prefersInteractive) {
          colors[colorKey].description = "Balanced Learner - Thrives in organized yet dynamic environments";
          score = 0.9;
        } else if (prefersStructured) {
          colors[colorKey].description = "Systematic Learner - Excels with clear structure and routines";
          score = 0.7;
        } else if (prefersInteractive) {
          colors[colorKey].description = "Dynamic Learner - Flourishes in active, varied learning spaces";
          score = 0.6;
        } else {
          colors[colorKey].description = "Flexible Learner - Adapts to different learning contexts";
          score = 0.4;
        }
      }
      
      // Communication Style
      else if (category === 'communicationstyle') {
        const prefersVerbal = answers.some(a => a.includes('verbal') && (a === 'Strongly Agree' || a === 'Agree'));
        const prefersWritten = answers.some(a => a.includes('written') && (a === 'Strongly Agree' || a === 'Agree'));
        
        if (prefersVerbal && prefersWritten) {
          colors[colorKey].description = "Versatile Communicator - Equally strong in written and verbal expression";
          score = 0.9;
        } else if (prefersVerbal) {
          colors[colorKey].description = "Verbal Explorer - Excels in spoken dialogue and discussion";
          score = 0.7;
        } else if (prefersWritten) {
          colors[colorKey].description = "Written Articulator - Strongest in written expression and documentation";
          score = 0.6;
        } else {
          colors[colorKey].description = "Mindful Communicator - Thoughtful and selective in communication";
          score = 0.4;
        }
      }
      
      colors[colorKey].intensity = score;
    }
  });

  return colors;
};

const getRecommendations = (auraColors: ReturnType<typeof calculateAuraColors>) => {
  const recommendations = [];
  
  // Enhanced recommendations based on detailed profiles
  if (auraColors.participationcomfort.description.includes('Active Engager')) {
    recommendations.push({
      type: 'seating',
      text: 'Optimal seating in the front rows to maximize engagement and facilitate active participation'
    });
  } else if (auraColors.participationcomfort.description.includes('Reflective Observer')) {
    recommendations.push({
      type: 'seating',
      text: 'Consider middle-row seating for balanced observation and participation opportunities'
    });
  }

  if (auraColors.learningenvironment.description.includes('Dynamic Learner')) {
    recommendations.push({
      type: 'collaboration',
      text: 'Seek out varied group projects and interactive learning opportunities'
    });
  } else if (auraColors.learningenvironment.description.includes('Systematic Learner')) {
    recommendations.push({
      type: 'collaboration',
      text: 'Create structured study schedules and organized learning environments'
    });
  }

  if (auraColors.communicationstyle.description.includes('Versatile Communicator')) {
    recommendations.push({
      type: 'interaction',
      text: 'Take leadership roles in group discussions and written project documentation'
    });
  } else if (auraColors.communicationstyle.description.includes('Written Articulator')) {
    recommendations.push({
      type: 'interaction',
      text: 'Leverage written communication channels and documentation opportunities'
    });
  }

  return recommendations;
};

const AuraSummary: React.FC<AuraSummaryProps> = ({ responses, onEditSurvey, onBackToSelection }) => {
  const navigate = useNavigate();
  const auraColors = calculateAuraColors(responses);
  const recommendations = getRecommendations(auraColors);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-lime-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden p-8"
        >
          <h1 className="text-3xl font-bold text-center mb-8">Your Learning Color Aura</h1>

          {/* Learning Color Aura Ring Visualization */}
          <div className="relative w-64 h-64 mx-auto mb-12">
            {/* Background circle */}
            <div className="absolute inset-0 rounded-full bg-gray-50" />
            
            {/* Aura bands */}
            {Object.entries(auraColors).map(([category, { color, intensity }], index) => {
              const bandWidth = 20; // Width of each band in pixels
              const gap = 4; // Gap between bands in pixels
              const radius = 32 - (index * (bandWidth + gap)); // Start from outer ring
              
              return (
                <motion.div
                  key={category}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ 
                    scale: 1,
                    opacity: 0.2 + (intensity * 0.8)
                  }}
                  transition={{ 
                    duration: 0.8,
                    delay: index * 0.2
                  }}
                  className="absolute rounded-full"
                  style={{
                    backgroundColor: color,
                    width: `${radius * 2}%`,
                    height: `${radius * 2}%`,
                    left: `${(100 - radius * 2) / 2}%`,
                    top: `${(100 - radius * 2) / 2}%`,
                  }}
                />
              );
            })}
          </div>

          {/* Aura Interpretation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {Object.entries(auraColors).map(([category, { color, intensity, label, description }]) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-gray-50 rounded-lg p-4"
              >
                <h3 className="font-semibold text-gray-900 mb-2">{label}</h3>
                <div className="h-2 bg-gray-200 rounded-full mb-2">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${intensity * 100}%`, backgroundColor: color }}
                  />
                </div>
                <p className="text-sm text-gray-700">{description}</p>
              </motion.div>
            ))}
          </div>

          {/* Recommendations */}
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900">Personalized Recommendations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendations.map((rec, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-4 rounded-lg"
                >
                  <p className="text-gray-800">{rec.text}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Collaboration Suggestions */}
          <div className="mt-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Collaboration Insights</h2>
            <p className="text-gray-600 mb-4">
              Based on your Learning Color Aura, you would work well with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Students with complementary communication styles for balanced group discussions</li>
              <li>Peers who share your learning environment preferences for effective collaboration</li>
              <li>Classmates with different participation comfort levels to create dynamic interactions</li>
            </ul>
          </div>

          <div className="mt-8 text-center space-x-4">
            {onEditSurvey && (
              <button
                onClick={onEditSurvey}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Edit Survey
              </button>
            )}
            {onBackToSelection && (
              <button
                onClick={onBackToSelection}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Take Another Survey
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Continue to Dashboard
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuraSummary;